import axios from "axios";
import { getAccessToken } from "./TokenRefresh";

const apiBase = process.env.REACT_APP_API_BASE_URL || "";

function normalizeEmail(email?: string) {
  return String(email || "").trim().toLowerCase();
}

function normalizeDigits(code?: string) {
  return String(code || "").replace(/\D/g, "");
}

/**
 * Adult/self OR student-start flow.
 * - Adult:
 *    - if user.email empty -> pass email to link it: startPurchaseReauthEmailCode(email)
 *    - else backend sends code and returns purchaseAuthId
 *    - backend may return alreadyAuthorized:true if a verified window exists
 * - Student:
 *    - backend returns requiresGuardian:true OR throws 403 w/ guardian reason
 */
export async function startPurchaseReauthEmailCode(email?: string) {
  const token = await getAccessToken();
  if (!token) throw new Error("No auth token");

  const normalizedEmail = normalizeEmail(email);

  const { data } = await axios.post(
    `${apiBase}/api/purchase/reauth/start`,
    {
      method: "email_code",
      ...(normalizedEmail ? { email: normalizedEmail } : {}),
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  return data;
}

/**
 * Adult/self verify endpoint (NOT for parent approval).
 * Requires purchaseAuthId returned by /reauth/start for adults.
 */
export async function verifyPurchaseReauthEmailCode(
  purchaseAuthId: string,
  code: string
) {
  const token = await getAccessToken();
  if (!token) throw new Error("No auth token");

  const cleaned = normalizeDigits(code);

  const { data } = await axios.post(
    `${apiBase}/api/purchase/reauth/verify`,
    { purchaseAuthId, method: "email_code", code: cleaned },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  return data;
}

/**
 * Student -> send parent invite + code.
 * Backend returns a NEW purchaseAuthId (this is the one you must use to verify the parent code).
 */
export async function startParentInviteEmailCode(parentEmail: string) {
  const token = await getAccessToken();
  if (!token) throw new Error("No auth token");

  const normalized = normalizeEmail(parentEmail);

  const { data } = await axios.post(
    `${apiBase}/api/purchase/reauth/start-parent-invite`,
    { parentEmail: normalized },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  return data;
}

/**
 * Student -> verify parent code on the kid device.
 * Must send the purchaseAuthId returned from startParentInviteEmailCode().
 */
export async function verifyParentPurchaseCode(
  purchaseAuthId: string,
  code: string
) {
  const token = await getAccessToken();
  if (!token) throw new Error("No auth token");

  const cleaned = normalizeDigits(code);

  const { data } = await axios.post(
    `${apiBase}/api/purchase/reauth/verify-parent-code`,
    { purchaseAuthId, code: cleaned },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  return data;
}
